/**
 * context/CrudContext.jsx
 * Global state for the forms list page via useReducer.
 */

import {
    createContext,
    useContext,
    useReducer,
    useCallback,
} from "@wordpress/element";

export const ACTIONS = {
    FETCH_START: "FETCH_START",
    FETCH_SUCCESS: "FETCH_SUCCESS",
    FETCH_ERROR: "FETCH_ERROR",
    SET_SEARCH: "SET_SEARCH",
    SET_STATUS: "SET_STATUS",
    SET_PAGE: "SET_PAGE",
    TOGGLE_SELECT: "TOGGLE_SELECT",
    SELECT_ALL: "SELECT_ALL",
    CLEAR_SELECTION: "CLEAR_SELECTION",
    UPDATE_ITEM: "UPDATE_ITEM",
    REMOVE_ITEM: "REMOVE_ITEM",
    REMOVE_ITEMS: "REMOVE_ITEMS",
    SET_OPEN_MENU: "SET_OPEN_MENU",
};

const initialState = {
    items: [],
    total: 0,
    page: 1,
    perPage: 10,
    search: "",
    status: "",
    selected: [],
    loading: false,
    loaded: false,
    error: null,
    openMenuId: null,
    limitReached: false,
};

function reducer(state, action) {
    switch (action.type) {
        case ACTIONS.FETCH_START:
            return { ...state, loading: true, error: null };

        case ACTIONS.FETCH_SUCCESS:
            return {
                ...state,
                loading: false,
                loaded: true,
                items: action.payload.items,
                total: action.payload.total,
                limitReached: action.payload.limitReached ?? false,
                selected: [],
            };

        case ACTIONS.FETCH_ERROR:
            return { ...state, loading: false, error: action.payload };

        case ACTIONS.SET_SEARCH:
            return { ...state, search: action.payload, page: 1, selected: [] };

        case ACTIONS.SET_STATUS:
            return { ...state, status: action.payload, page: 1, selected: [] };

        case ACTIONS.SET_PAGE:
            return { ...state, page: action.payload, selected: [] };

        case ACTIONS.TOGGLE_SELECT: {
            const id = action.payload;
            return {
                ...state,
                selected: state.selected.includes(id)
                    ? state.selected.filter((s) => s !== id)
                    : [...state.selected, id],
            };
        }

        case ACTIONS.SELECT_ALL:
            return {
                ...state,
                selected:
                    state.selected.length === state.items.length
                        ? []
                        : state.items.map((f) => f.id),
            };

        case ACTIONS.CLEAR_SELECTION:
            return { ...state, selected: [] };

        case ACTIONS.UPDATE_ITEM:
            return {
                ...state,
                items: state.items.map((f) =>
                    f.id === action.payload.id
                        ? { ...f, ...action.payload.changes }
                        : f,
                ),
            };

        case ACTIONS.REMOVE_ITEM:
            return {
                ...state,
                items: state.items.filter((f) => f.id !== action.payload),
                total: state.total - 1,
                selected: state.selected.filter((id) => id !== action.payload),
            };

        case ACTIONS.REMOVE_ITEMS:
            return {
                ...state,
                items: state.items.filter(
                    (f) => !action.payload.includes(f.id),
                ),
                total: state.total - action.payload.length,
                selected: [],
            };

        case ACTIONS.SET_OPEN_MENU:
            return { ...state, openMenuId: action.payload };

        default:
            return state;
    }
}

/**
 * Builds the URL to open the form builder for creating a new form.
 * Exported here so any component can link to the add new page
 * without duplicating the URL logic.
 *
 * Usage:  <a href={getAddNewUrl()}>...</a>
 */
export const getAddNewUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", "wpbean-product-gallery-rule");
    url.searchParams.delete("id");
    return url.toString();
};

/**
 * Builds the URL to open a form in the builder.
 * Exported here so any component can link to the edit page
 * without duplicating the URL logic.
 *
 * Usage:  <a href={getEditUrl(form.id)}>...</a>
 */
export const getEditUrl = (id) => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", "wpbean-product-gallery-rule");
    url.searchParams.set("id", id);
    return url.toString();
};

const StateCtx = createContext(null);
const DispatchCtx = createContext(null);

export const CrudProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    return (
        <StateCtx.Provider value={state}>
            <DispatchCtx.Provider value={dispatch}>
                {children}
            </DispatchCtx.Provider>
        </StateCtx.Provider>
    );
};

export const useCrudListState = () => useContext(StateCtx);
export const useCrudListDispatch = () => useContext(DispatchCtx);

export const useCrudList = () => {
    const state = useCrudListState();
    const dispatch = useCrudListDispatch();

    const actions = {
        fetchStart: useCallback(
            () => dispatch({ type: ACTIONS.FETCH_START }),
            [dispatch],
        ),
        fetchSuccess: useCallback(
            (d) => dispatch({ type: ACTIONS.FETCH_SUCCESS, payload: d }),
            [dispatch],
        ),
        fetchError: useCallback(
            (m) => dispatch({ type: ACTIONS.FETCH_ERROR, payload: m }),
            [dispatch],
        ),
        setSearch: useCallback(
            (v) => dispatch({ type: ACTIONS.SET_SEARCH, payload: v }),
            [dispatch],
        ),
        setStatus: useCallback(
            (v) => dispatch({ type: ACTIONS.SET_STATUS, payload: v }),
            [dispatch],
        ),
        setPage: useCallback(
            (v) => dispatch({ type: ACTIONS.SET_PAGE, payload: v }),
            [dispatch],
        ),
        toggleSelect: useCallback(
            (id) => dispatch({ type: ACTIONS.TOGGLE_SELECT, payload: id }),
            [dispatch],
        ),
        selectAll: useCallback(
            () => dispatch({ type: ACTIONS.SELECT_ALL }),
            [dispatch],
        ),
        clearSelection: useCallback(
            () => dispatch({ type: ACTIONS.CLEAR_SELECTION }),
            [dispatch],
        ),
        updateItem: useCallback(
            (id, changes) =>
                dispatch({
                    type: ACTIONS.UPDATE_ITEM,
                    payload: { id, changes },
                }),
            [dispatch],
        ),
        removeItem: useCallback(
            (id) => dispatch({ type: ACTIONS.REMOVE_ITEM, payload: id }),
            [dispatch],
        ),
        removeItems: useCallback(
            (ids) => dispatch({ type: ACTIONS.REMOVE_ITEMS, payload: ids }),
            [dispatch],
        ),
        setOpenMenu: useCallback(
            (id) => dispatch({ type: ACTIONS.SET_OPEN_MENU, payload: id }),
            [dispatch],
        ),
    };

    return { state, actions };
};
